import {CompactSelection, GridCell, GridCellKind, GridSelection, Theme} from "@glideapps/glide-data-grid";
import {DataGridContentColumn as GridColumnWithContent} from "./data-grid-types";
import {getDataGridSeparatorTheme} from "./dataGridTheme";

export type DataGridSeparator = {value: string; displayValue: string; sortValue?: string};
export type DataGridDisplayRow<T> =
  | {type: "SEPARATOR"; separator: DataGridSeparator}
  | {type: "DATA"; value: T; sourceIndex: number};

export const DATA_GRID_SEPARATOR_THEME: Partial<Theme> = getDataGridSeparatorTheme();

export function filterDataGridRecordSelection(
  selection: GridSelection,
  isRecord: (row: number) => boolean
): GridSelection {
  return {
    ...selection,
    rows: selection.rows
      .toArray()
      .filter(isRecord)
      .reduce((rows, row) => rows.add(row), CompactSelection.empty()),
    current: selection.current && isRecord(selection.current.cell[1]) ? selection.current : undefined,
  };
}

/** Group a copy of the source, retaining stable original indices for record actions. */
export function getDataGridDisplayRows<T>(
  data: readonly T[],
  getSeparator?: (value: T) => DataGridSeparator
): DataGridDisplayRow<T>[] {
  if (!getSeparator) return data.map((value, sourceIndex) => ({type: "DATA", value, sourceIndex}));
  const groups = new Map<string, {separator: DataGridSeparator; rows: DataGridDisplayRow<T>[]}>();
  data.forEach((value, sourceIndex) => {
    const separator = getSeparator(value);
    let group = groups.get(separator.value);
    if (!group) {
      group = {separator, rows: []};
      groups.set(separator.value, group);
    }
    group.rows.push({type: "DATA", value, sourceIndex});
  });
  return [...groups.values()]
    .sort(
      (a, b) =>
        (a.separator.sortValue ?? a.separator.displayValue).localeCompare(
          b.separator.sortValue ?? b.separator.displayValue,
          undefined,
          {numeric: true}
        ) || a.separator.value.localeCompare(b.separator.value)
    )
    .flatMap((group) => [{type: "SEPARATOR" as const, separator: group.separator}, ...group.rows]);
}

export function getDataGridSeparatorColumns<T>(
  columns: readonly GridColumnWithContent<T>[],
  grouped: boolean,
  renderSeparator?: (separator: DataGridSeparator, columnIndex: number, columnCount: number) => GridCell,
  separatorTheme: Partial<Theme> = DATA_GRID_SEPARATOR_THEME
): GridColumnWithContent<DataGridDisplayRow<T>>[] {
  return columns.map((column, index) => ({
    ...column,
    hasMenu: !grouped && column.hasMenu,
    getSortValue: column.getSortValue
      ? (row) => (row.type === "DATA" ? column.getSortValue?.(row.value) : undefined)
      : undefined,
    getContent: (row) =>
      row.type === "DATA"
        ? column.getContent(row.value, index)
        : renderSeparator
        ? renderSeparator(row.separator, index, columns.length)
        : {
            kind: GridCellKind.Text,
            data: index === 0 ? row.separator.displayValue : "",
            displayData: index === 0 ? row.separator.displayValue : "",
            allowOverlay: false,
            readonly: true,
            span: [0, columns.length - 1],
            themeOverride: separatorTheme,
          },
  }));
}
